import React, { memo, useState, useEffect } from 'react';
import { Button, Flex, Text, Collapse, FlexColumn, Callout, Tooltip, Popover } from 'core/components';
import { TextArea } from 'core/form';
import CopyToClipboard from 'app/components/CopyToClipboardButton';
import { FiCopy, FiThumbsUp, FiThumbsDown } from 'react-icons/fi';
import Markdown from '../../journeys/Markdown';
import { ReasoningDetails, ReasoningContent, StepContainer } from '../KaizenChatSession.styles';
import KaizenToolResult from '../KaizenToolResult';
import FeedbackModel from '../models/FeedbackModel';

// Memoized step component to prevent unnecessary re-renders
const StepComponent = memo(
  ({
    step,
    stepIndex,
    turn,
    $auth,
    isStepReasoningOpen,
    onToggleReasoning,
    onSelectToolResult,
    isCopilotMode,
    isExistingChatLoading,
    sessionModel
  }) => {
    const stepId = step.id || stepIndex;
    const { llm_response = {}, toolCalls } = step;
    const hasFinalAnswer = llm_response?.final_answer && step.processing_status === 'COMPLETED';
    let internal_error_message = false;

    // This should never happen, but just to be safe.
    if (
      !llm_response.final_answer &&
      !llm_response.reasoning &&
      !step.error_message &&
      step.processing_status === 'COMPLETED'
    ) {
      internal_error_message = true;
    }

    const [showThankYouMessage, setShowThankYouMessage] = useState(false);
    const [showFeedbackPopover, setShowFeedbackPopover] = useState(false);

    // Memoize feedbackModel so it's only created when dependencies change
    const feedbackModel = React.useMemo(() => {
      if (hasFinalAnswer) {
        const currentUserFeedbackRecord = turn.feedback?.find(
          (item) => String(item.user_id) === String($auth.activeUser.id)
        );
        if (currentUserFeedbackRecord) {
          return new FeedbackModel(currentUserFeedbackRecord);
        }
        return new FeedbackModel();
      }
      return null;
    }, [hasFinalAnswer, turn.feedback, $auth.activeUser.id]);

    const [feedbackText, setFeedbackText] = useState(feedbackModel?.get('user_comment'));

    const handleThumbsUpClick = () => {
      if (feedbackModel.get('user_rating') === 1) {
        return;
      }
      setShowThankYouMessage(true);
      feedbackModel.save(
        { user_rating: 1, agent_turn_id: turn.id },
        { sendProvidedValuesOnly: true, toast: false, optimistic: true }
      );
    };

    const handleThumbsDownClick = () => {
      if (feedbackModel.get('user_rating') === -1) {
        setShowFeedbackPopover(true);
        return;
      }
      setShowThankYouMessage(true);
      setShowFeedbackPopover(true);
      feedbackModel.save(
        { user_rating: -1, agent_turn_id: turn.id },
        { sendProvidedValuesOnly: true, toast: false, optimistic: true }
      );
    };

    const handleFeedbackSubmit = () => {
      // Submit the feedback with optional text
      setShowFeedbackPopover(false);
      setShowThankYouMessage(true);
      feedbackModel.save(
        { user_comment: String(feedbackText).trim().substring(0, 5000), agent_turn_id: turn.id },
        { sendProvidedValuesOnly: false, toast: false, optimistic: true }
      );
    };

    const handlePopoverClose = () => {
      setShowFeedbackPopover(false);
      // setFeedbackText('');
    };

    // Hide thank you message after 3 seconds
    useEffect(() => {
      if (showThankYouMessage) {
        const timer = setTimeout(() => {
          setShowThankYouMessage(false);
        }, 3000);

        return () => clearTimeout(timer);
      }
      return undefined;
    }, [showThankYouMessage]);

    const isFinalAnswer = llm_response?.final_answer && step.processing_status === 'COMPLETED';

    return (
      <StepContainer>
        {llm_response?.reasoning && (
          <Flex alignItems="center" gap={1}>
            <Button
              small
              minimal
              color="muted"
              icon={isStepReasoningOpen ? 'caret-up' : 'caret-down'}
              style={{ alignSelf: 'flex-start' }}
              text={isStepReasoningOpen ? 'Hide reasoning' : 'Show reasoning'}
              onClick={() => onToggleReasoning(stepId)}
            />
          </Flex>
        )}
        <Flex flexDirection="column" gap={1}>
          {llm_response?.reasoning && (
            <Collapse isOpen={isStepReasoningOpen} title="Reasoning" defaultOpen>
              <ReasoningDetails>
                <ReasoningContent>
                  <Markdown>{llm_response.reasoning}</Markdown>
                </ReasoningContent>
              </ReasoningDetails>
            </Collapse>
          )}

          {isFinalAnswer && (
            <Flex flexDirection="column" gap={1}>
              <Markdown>{llm_response.final_answer}</Markdown>
              <Flex alignItems="center" gap={1}>
                <Tooltip content="Copy response" position="bottom">
                  <CopyToClipboard intent="default" text={llm_response.final_answer}>
                    <Button minimal icon={FiCopy} />
                  </CopyToClipboard>
                </Tooltip>

                <Button
                  minimal
                  icon={FiThumbsUp}
                  intent={feedbackModel.get('user_rating') === 1 ? 'success' : 'default'}
                  onClick={() => handleThumbsUpClick()}
                />

                <Popover
                  isOpen={showFeedbackPopover}
                  onClose={handlePopoverClose}
                  position="top"
                  minimal={false}
                  content={
                    <Flex flexDirection="column" gap={1} p={2} style={{ width: '330px', height: 'auto' }}>
                      <Text fontWeight="bold">Help us improve AI Advisor!</Text>
                      <Text color="muted" fontSize={13}>
                        Tell us what went wrong
                      </Text>
                      <TextArea
                        placeholder="Your feedback..."
                        value={feedbackText}
                        rows={2}
                        onChange={(e) => setFeedbackText(e.target.value)}
                        style={{ resize: 'none' }}
                        autoFocus
                      />
                      <Flex justifyContent="flex-end" gap={1}>
                        <Button minimal text="Cancel" onClick={handlePopoverClose} />
                        <Button intent="primary" text="Submit" onClick={handleFeedbackSubmit} />
                      </Flex>
                    </Flex>
                  }
                >
                  <Button
                    minimal
                    icon={FiThumbsDown}
                    intent={feedbackModel.get('user_rating') === -1 ? 'danger' : 'default'}
                    onClick={() => handleThumbsDownClick('down')}
                  />
                </Popover>

                {showThankYouMessage && (
                  <Text fontSize={13} color="muted">
                    Thank you! Your feedback helps make AI Advisor better for everyone.
                  </Text>
                )}
              </Flex>
            </Flex>
          )}

          {toolCalls?.length > 0 && (
            <FlexColumn flexDirection="column" gap="8px">
              {toolCalls?.map((toolCall) => (
                <KaizenToolResult
                  key={toolCall.id || toolCall.tool_tracking_id}
                  tool={toolCall}
                  step={step}
                  onSelectToolResult={onSelectToolResult}
                  isCopilotMode={isCopilotMode}
                  isExistingChatLoading={isExistingChatLoading}
                  sessionModel={sessionModel}
                />
              ))}
            </FlexColumn>
          )}
          {(step.error_message || internal_error_message) && step?.error_message !== 'SCHEMA_FAILED' && (
            <Callout intent="danger">
              <Text small>Error: something went wrong</Text>
            </Callout>
          )}
        </Flex>
      </StepContainer>
    );
  }
);

StepComponent.displayName = 'StepComponent';

export default StepComponent;
